import React, { useEffect, useState } from "react";
import {
  Badge,
  Card,
  Dropdown,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { Col } from "react-bootstrap";
import {
  BsDashCircleFill,
  BsHeartFill,
  BsInfoCircle,
  BsPlusCircleFill,
} from "react-icons/bs";
import { AdminPublicInfo } from "../../../../Api/Admin/models";
import { useLoadCoins } from "../../../../Hooks/Coins/useLoadCoins";
import { GetCbuResponse } from "../../../../Api/CBU/models";
import LocalHistoryModal from "./LocalHistoryModal";
import { useLoadPromo } from "../../../../Hooks/Coins/useLoadPromo";
import { useHistory } from "../../../../Hooks/Coins/useHistory";
import { BiLoaderCircle } from "react-icons/bi";
import { useSubtractCoins } from "../../../../Hooks/Coins/useSubtractCoins";
import { useBalance } from "../../../../Hooks/useBalance";
import { MdOutlineCurrencyExchange } from "react-icons/md";
import { CompletePlayerInfoModal } from "./CompletePlayerInfoModal";
import { ConfirmBonificationNotUsed } from "../../../../Components/ConfirmBonificationNotUsed";

interface Props {
  adminInfo: AdminPublicInfo | null;
  fetchedData: GetCbuResponse | null;
}

export const LoadCoins = ({ adminInfo, fetchedData }: Props) => {
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCompletePlayerInfoModal, setShowCompletePlayerInfoModal] =
    useState(false);

  const [selectedPlayerToUpdate, setSelectedPlayerToUpdate] = useState<
    any | null
  >(null);

  const [lastUploadedValue, setLastUploadedValue] = useState<any | null>(null);
  const [form, setForm] = useState({
    name: "",
    quantity: "",
    senderName: "",
    destinationAccountName: fetchedData?.titular ?? "",
  });

  // Bonification Modal Confirmation
  const [showConfirmBonificationNotUsed, setShowConfirmBonificationNotUsed] =
    useState(false);
  const [bonificationsData, setBonificationsData] = useState<any | null>(null);

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const {
    historial,
    errorQuantities,
    showHistory,
    setShowHistory,
    setErrorQuantities,
    setHistorial,
  } = useHistory();

  const { confirmAddCoins } = useLoadCoins({
    adminInfo,
    setForm,
    setLoading,
    setDropdownOpen,
    fetchedData,
    form,
    loading,
    setHistorial,
    historial,
    setErrorQuantities,
    errorQuantities,
    showHistory,
    setShowHistory,
    setSelectedPlayerToUpdate,
    setShowCompletePlayerInfoModal,
    setLastUploadedValue,
  });

  const { confirmAddPromo } = useLoadPromo({
    adminInfo,
    setForm,
    setLoading,
    setDropdownOpen,
    fetchedData,
    form,
    loading,
    setHistorial,
    historial,
    setErrorQuantities,
    errorQuantities,
    showHistory,
    setShowHistory,
    setSelectedPlayerToUpdate,
    setShowCompletePlayerInfoModal,
  });

  const { handleSubtractCoins } = useSubtractCoins({
    adminInfo,
    setForm,
    setLoading,
    setDropdownOpen,
    fetchedData,
    form,
    loading,
    setHistorial,
    historial,
    setErrorQuantities,
    errorQuantities,
    showHistory,
    setShowHistory,
    setShowConfirmBonificationNotUsed,
    setBonificationsData,
  });

  const { balance, getBalance, loadingBalance, formatBalance } = useBalance({
    showBalance: adminInfo?.showBalance ?? false,
  });

  useEffect(() => {
    if (fetchedData?.titular) {
      setForm({ ...form, destinationAccountName: fetchedData?.titular });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedData]);

  return (
    <div className="w-100">
      {showCompletePlayerInfoModal && selectedPlayerToUpdate && (
        <CompletePlayerInfoModal
          show={showCompletePlayerInfoModal}
          handleClose={() => setShowCompletePlayerInfoModal(false)}
          playerInfo={selectedPlayerToUpdate}
          quantityToLoad={Number(lastUploadedValue)}
        />
      )}

      {showConfirmBonificationNotUsed && bonificationsData && (
        <ConfirmBonificationNotUsed
          show={showConfirmBonificationNotUsed}
          handleClose={() => setShowConfirmBonificationNotUsed(false)}
          bonificationsData={bonificationsData}
          form={form}
          handleSubtractCoins={handleSubtractCoins}
        />
      )}

      <Card className="px-3 text-black py-3">
        <div className="d-flex justify-content-between">
          <div className="mb-1 d-flex justify-content-start text-primary px-1 text-xs text-center text-white mx-2">
            Panel de Carga
          </div>

          {adminInfo?.showBalance && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Actualizar saldo</Tooltip>}
            >
              <div className="text-secondary text-xxs">
                <div
                  className="d-flex align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={getBalance}
                >
                  <div className="text-xs d-flex align-items-center gap-1 text-primary">
                    <div className="mx-1">
                      {loadingBalance
                        ? "Actualizando.."
                        : `Saldo: ${formatBalance(balance)}`}
                    </div>
                    <MdOutlineCurrencyExchange />
                  </div>
                </div>
              </div>
            </OverlayTrigger>
          )}
        </div>

        <Row className="mb-2 d-flex justify-content-center g-0">
          <Col className="mx-0 px-1">
            <input
              value={form.name}
              onChange={inputHandler}
              name="name"
              type="text"
              className="form-control mx-0"
              placeholder="Usuario"
              aria-label="Recipient's username"
              aria-describedby="basic-addon2"
            />
          </Col>
          <Col className="mx-0 px-1">
            <input
              value={form.quantity}
              onChange={inputHandler}
              name="quantity"
              type="text"
              className="form-control mx-0"
              placeholder="Cantidad"
              aria-label="Recipient's username"
              aria-describedby="basic-addon2"
              list="quantity-options"
              autoComplete="off"
            />
            <datalist id="quantity-options">
              <option value="1000"></option>
              <option value="1500"></option>
              <option value="2000"></option>
              <option value="2500"></option>
              <option value="3000"></option>
              <option value="4000"></option>
              <option value="5000"></option>
              <option value="10000"></option>
            </datalist>
          </Col>
        </Row>
        <Row className="mb-2 d-flex justify-content-center g-0">
          <Col className="mx-0 px-1">
            <input
              maxLength={50}
              value={form.senderName}
              onChange={inputHandler}
              name="senderName"
              type="text"
              className="form-control mx-0"
              placeholder="Nombre Remitente"
              aria-label="Nombre remitente"
              aria-describedby="basic-addon2"
            />
            <div className="d-flex text-secondary g-1 align-items-center text-xxs">
              <BsInfoCircle size={10} className="mx-1" /> Nombre de quien envía.
              <span className="mx-1" style={{ fontSize: "9px" }}>
                (Opcional)
              </span>
            </div>
          </Col>
          <Col className="mx-0 px-1">
            <input
              maxLength={50}
              value={form.destinationAccountName}
              onChange={inputHandler}
              name="destinationAccountName"
              type="text"
              className="form-control mx-0"
              placeholder="Cuenta Destino"
              aria-label="Recipient's username"
              aria-describedby="basic-addon2"
              autoComplete="off"
            />
            <div className="d-flex text-secondary g-1 align-items-center text-xxs">
              <BsInfoCircle size={10} className="mx-1" /> Nombre de quien
              recibe.
              <span className="mx-1" style={{ fontSize: "9px" }}>
                (Opcional)
              </span>
            </div>
          </Col>
        </Row>

        <Row className="mb-2 d-flex justify-content-center g-0 mt-2">
          <Col className="action-button p-2 mx-1 rounded d-flex">
            <div
              onClick={(e) => confirmAddCoins({ isBonus: false })}
              className="w-100 d-flex flex-column align-items-center gap-2"
            >
              {loading ? (
                <BiLoaderCircle size={18} />
              ) : (
                <BsPlusCircleFill size={18} />
              )}
              <Row className="d-flex justify-content-center">
                <span className="text-center">
                  {loading ? "Cargando.." : "Cargar"}
                </span>
              </Row>
            </div>
          </Col>

          <Col className="action-button p-2 mx-1 rounded d-flex">
            <div
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-100 d-flex flex-column align-items-center gap-2"
            >
              {loading ? (
                <BiLoaderCircle size={18} />
              ) : (
                <BsHeartFill size={18} />
              )}
              <Row className="d-flex justify-content-center">
                <span className="text-center">
                  {loading ? "Cargando.." : "Bonificaciones"}
                </span>
              </Row>
            </div>

            <Dropdown show={dropdownOpen}>
              <Dropdown.Menu className="text-white dark-dropdown-custom ">
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 10 })}
                >
                  Bonificar 10%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 15 })}
                >
                  Bonificar 15%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 20 })}
                >
                  Bonificar 20%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 25 })}
                >
                  Bonificar 25%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 30 })}
                >
                  Bonificar 30%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 35 })}
                >
                  Bonificar 35%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 40 })}
                >
                  Bonificar 40%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 50 })}
                >
                  Bonificar 50%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 60 })}
                >
                  Bonificar 60%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 70 })}
                >
                  Bonificar 70%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 80 })}
                >
                  Bonificar 80%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 90 })}
                >
                  Bonificar 90%
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-secondary"
                  onClick={(event) => confirmAddPromo({ bonus: 100 })}
                >
                  Bonificar 100% (x2)
                </Dropdown.Item>

                
                {adminInfo?.platform !== "ganaencasa" && (
                  <>
                    <hr />
                  <Dropdown.Item
                    className="text-secondary"
                    onClick={(event) => confirmAddCoins({ isBonus: true })}
                  >
                    Bonificación total
                  </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col className="action-button p-2 mx-1 rounded d-flex">
            <div
              onClick={() => handleSubtractCoins()}
              className="w-100 d-flex flex-column align-items-center gap-2"
            >
              {loading ? (
                <BiLoaderCircle size={18} />
              ) : (
                <BsDashCircleFill size={18} />
              )}
              <Row className="d-flex justify-content-center">
                <span className="text-center">
                  {loading ? "Cargando.." : "Bajar"}
                </span>
              </Row>
            </div>
          </Col>
        </Row>
        {historial?.length > 0 && (
          <div
            className="text-end text-secondary mx-2 text-xxs text-primary"
            role="button"
            onClick={() => setShowHistory(!showHistory)}
          >
            {errorQuantities > 0 ? (
              <span className="text-danger">
                {errorQuantities} transacciones con errores
              </span>
            ) : (
              ""
            )}{" "}
            Mostrar Historial
          </div>
        )}

        {showHistory && (
          <LocalHistoryModal
            historial={historial}
            show={showHistory}
            onHide={() => setShowHistory(false)}
            setErrorQuantities={setErrorQuantities}
          />
        )}
      </Card>
    </div>
  );
};
